import { useCallback } from 'react';
import {
  InterceptModalAutoClose,
  InterceptModalAutoCloseProps,
} from '@bridebook/web/components/common/intercept-modal/intercept-modal-auto-close';
import { usePopupRoundedModal } from './popup-rounded-modal-context';

/**
 * Options for configuring an intercept modal.
 * Extends InterceptModalAutoCloseProps but omits the closeModal prop as it's handled internally.
 */
type IInterceptModalOptions = Omit<InterceptModalAutoCloseProps, 'closeModal'> & {
  onCloseModal?: () => void;
};

/**
 * Hook for managing intercept modals - confirmation-style modals with primary and secondary actions.
 *
 * @example
 * ```tsx
 * const { openModal } = useInterceptModal();
 *
 * // Open a confirmation modal
 * openModal('delete-confirmation', {
 *   title: 'Delete Item',
 *   text: 'Are you sure you want to delete this item?',
 *   primaryButtonText: 'Delete',
 *   onPrimaryButtonClick: async () => {
 *     await deleteItem();
 *   },
 *   secondaryButtonText: 'Cancel'
 * });
 * ```
 * In the example above, the closeModal function is not passed to the InterceptModalContent component,
 * because it's handled internally by the useInterceptModal hook.
 * Additionally the onPrimaryButtonClick is async, so the button will be shown as loading until the promise is resolved. No need to pass that separatelly. But if you need you can overwrite it via primaryButtonProps.
 * onSecondaryButtonClick is optional, because in most cases secondary button only close the modal. But if you need something else (like analytics) you can use it.
 *
 * @returns {Object} Modal control functions
 * @returns {Function} openModal - Opens an intercept modal with the given options
 * @returns {Function} closeModal - Closes the currently open modal
 */
export const useInterceptModal = () => {
  const { openModal: _openModal, closeModal: _closeModal } = usePopupRoundedModal();

  /**
   * Opens an intercept modal with the specified configuration.
   *
   * @param {string} id - Unique identifier for the modal instance
   * @param {IInterceptModalOptions} options - Configuration options for the modal
   * @param {string} options.title - Modal title
   * @param {string} [options.text] - Optional modal content text
   * @param {string} options.primaryButtonText - Text for the primary action button
   * @param {Function} options.onPrimaryButtonClick - Async function called when primary button is clicked
   * @param {string} options.secondaryButtonText - Text for the secondary action button
   * @param {Function} [options.onSecondaryButtonClick] - Optional async function called when secondary button is clicked
   * @param {Object} [options.primaryButtonProps] - Optional additional props for primary button
   * @param {Object} [options.secondaryButtonProps] - Optional additional props for secondary button
   */
  const openModal = useCallback(
    (id: string, options: IInterceptModalOptions) => {
      const { onCloseModal, ...rest } = options;
      const handleCloseModal = () => {
        _closeModal();
        onCloseModal?.();
      };
      _openModal(id, <InterceptModalAutoClose {...rest} closeModal={handleCloseModal} />);
    },
    [_openModal, _closeModal],
  );

  return { openModal, closeModal: _closeModal };
};
